/**
 * Revenue Chart component for displaying quarterly revenue trends
 * Features:
 * - Stacked bar chart when segment data is available
 * - Simple bars showing total revenue otherwise
 * - Forward-looking quarter in gray (forecast/guidance)
 * - Interactive hover with segment details
 * - 12 trailing quarters + 1 forecast quarter
 */

import { useState, useRef, useCallback } from 'react';
import type { QuarterlyRevenue } from '../../types';

interface RevenueChartProps {
  data: QuarterlyRevenue;
  isLoading?: boolean;
}

// Color palette for segments
const SEGMENT_COLORS = [
  '#E30B5C', // Raspberry
  '#10B981', // Emerald
  '#F59E0B', // Amber
  '#EC4899', // Pink
  '#8B5CF6', // Violet
  '#14B8A6', // Teal
  '#F97316', // Orange
  '#06B6D4', // Cyan
];

function formatBillions(value: number | null): string {
  if (value === null) return 'N/A';
  const billions = value / 1_000_000_000;
  if (billions >= 100) return `$${billions.toFixed(0)}B`;
  if (billions >= 10) return `$${billions.toFixed(1)}B`;
  return `$${billions.toFixed(2)}B`;
}

function formatShortBillions(value: number | null): string {
  if (value === null) return 'N/A';
  const billions = value / 1_000_000_000;
  if (billions >= 100) return `${billions.toFixed(0)}B`;
  if (billions >= 10) return `${billions.toFixed(1)}B`;
  return `${billions.toFixed(2)}B`;
}

interface HoverInfo {
  index: number;
  isForecast: boolean;
  quarter: string;
  totalRevenue: number | null;
  segments: { name: string; value: number | null; color: string }[];
  x: number;
  y: number;
}

export function RevenueChart({ data, isLoading }: RevenueChartProps) {
  const [hoverInfo, setHoverInfo] = useState<HoverInfo | null>(null);
  const svgRef = useRef<SVGSVGElement>(null);

  const width = 700;
  const height = 300;
  const padding = { top: 40, right: 30, bottom: 60, left: 70 };
  const chartWidth = width - padding.left - padding.right;
  const chartHeight = height - padding.top - padding.bottom;

  // Include forecast quarter if available
  const hasSegments = data.segments.length > 0;
  const hasForecast = data.forecast !== null;
  const totalQuarters = data.quarters.length + (hasForecast ? 1 : 0);

  // Calculate max revenue for scale
  const allRevenues = [...data.total_revenue.filter((v): v is number => v !== null)];
  if (hasForecast && data.forecast) {
    allRevenues.push(data.forecast.total_revenue);
  }
  const maxRevenue = Math.max(...allRevenues, 1);

  const barWidth = (chartWidth / totalQuarters) * 0.7;
  const barGap = (chartWidth / totalQuarters) * 0.15;

  const getBarX = (index: number) => padding.left + index * (chartWidth / totalQuarters) + barGap;
  const getBarHeight = (value: number | null) => {
    if (value === null) return 0;
    return (value / maxRevenue) * chartHeight;
  };

  const handleMouseMove = useCallback((e: React.MouseEvent<SVGSVGElement>) => {
    if (!svgRef.current || data.quarters.length === 0) return;

    const rect = svgRef.current.getBoundingClientRect();
    const x = e.clientX - rect.left - padding.left;
    const y = e.clientY - rect.top;

    const barTotalWidth = chartWidth / totalQuarters;
    const dataIndex = Math.floor(x / barTotalWidth);
    const clampedIndex = Math.max(0, Math.min(totalQuarters - 1, dataIndex));

    const isForecast = hasForecast && clampedIndex === data.quarters.length;
    const quarter = isForecast && data.forecast
      ? data.forecast.quarter
      : data.quarters[clampedIndex] || '';
    const totalRevenue = isForecast && data.forecast
      ? data.forecast.total_revenue
      : data.total_revenue[clampedIndex] ?? null;

    // Build segment info
    const segments: { name: string; value: number | null; color: string }[] = [];
    if (hasSegments) {
      data.segments.forEach((segmentName, i) => {
        const value = isForecast && data.forecast
          ? data.forecast.segments[segmentName] ?? null
          : data.segment_data[segmentName]?.[clampedIndex] ?? null;
        segments.push({
          name: segmentName,
          value,
          color: SEGMENT_COLORS[i % SEGMENT_COLORS.length] ?? '#E30B5C',
        });
      });
    }

    setHoverInfo({
      index: clampedIndex,
      isForecast,
      quarter,
      totalRevenue,
      segments,
      x: e.clientX - rect.left,
      y,
    });
  }, [data, totalQuarters, hasSegments, hasForecast, chartWidth, padding.left]);

  const handleMouseLeave = useCallback(() => {
    setHoverInfo(null);
  }, []);

  if (isLoading) {
    return (
      <div className="bg-white rounded-lg border border-gray-200 p-6">
        <div className="animate-pulse">
          <div className="h-6 w-32 bg-gray-200 rounded mb-4" />
          <div className="h-64 bg-gray-100 rounded" />
        </div>
      </div>
    );
  }

  if (data.quarters.length === 0) {
    return (
      <div className="bg-white rounded-lg border border-gray-200 p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Revenue</h3>
        <p className="text-gray-500 text-center py-8">No quarterly revenue data available</p>
      </div>
    );
  }

  // Y-axis labels
  const yLabels = [maxRevenue, maxRevenue * 0.75, maxRevenue * 0.5, maxRevenue * 0.25, 0];

  // Render segment stacks or simple bars
  const renderBars = () => {
    const bars: JSX.Element[] = [];

    // Historical quarters
    data.quarters.forEach((_quarter, idx) => {
      const x = getBarX(idx);
      const isHovered = hoverInfo?.index === idx && !hoverInfo?.isForecast;

      if (hasSegments) {
        // Stacked bars for segments
        let currentY = padding.top + chartHeight;
        data.segments.forEach((segmentName, segIdx) => {
          const value = data.segment_data[segmentName]?.[idx] ?? null;
          if (value !== null) {
            const barH = getBarHeight(value);
            currentY -= barH;
            bars.push(
              <rect
                key={`bar-${idx}-${segIdx}`}
                x={x}
                y={currentY}
                width={barWidth}
                height={barH}
                fill={isHovered
                  ? SEGMENT_COLORS[segIdx % SEGMENT_COLORS.length]
                  : SEGMENT_COLORS[segIdx % SEGMENT_COLORS.length] + 'DD'}
                className="transition-all duration-150"
              />
            );
          }
        });
      } else {
        // Simple bar for total revenue
        const totalRev = data.total_revenue[idx] ?? null;
        if (totalRev !== null) {
          const barH = getBarHeight(totalRev);
          bars.push(
            <rect
              key={`bar-${idx}`}
              x={x}
              y={padding.top + chartHeight - barH}
              width={barWidth}
              height={barH}
              fill={isHovered ? '#B8094A' : '#E30B5C'}
              rx={2}
              className="transition-colors duration-150"
            />
          );
        }
      }
    });

    // Forecast quarter (in gray)
    if (hasForecast && data.forecast) {
      const idx = data.quarters.length;
      const x = getBarX(idx);
      const isHovered = hoverInfo?.index === idx && hoverInfo?.isForecast;

      if (hasSegments) {
        let currentY = padding.top + chartHeight;
        data.segments.forEach((segmentName, segIdx) => {
          const value = data.forecast!.segments[segmentName] ?? null;
          if (value !== null) {
            const barH = getBarHeight(value);
            currentY -= barH;
            bars.push(
              <rect
                key={`forecast-${segIdx}`}
                x={x}
                y={currentY}
                width={barWidth}
                height={barH}
                fill={isHovered ? '#6B7280' : '#9CA3AF'}
                className="transition-all duration-150"
                strokeDasharray="4 2"
              />
            );
          }
        });
      } else {
        const barH = getBarHeight(data.forecast.total_revenue);
        bars.push(
          <rect
            key="forecast-total"
            x={x}
            y={padding.top + chartHeight - barH}
            width={barWidth}
            height={barH}
            fill={isHovered ? '#6B7280' : '#9CA3AF'}
            rx={2}
            className="transition-colors duration-150"
          />
        );
      }

      // Forecast indicator pattern (diagonal stripes)
      bars.push(
        <rect
          key="forecast-overlay"
          x={x}
          y={padding.top + chartHeight - getBarHeight(data.forecast.total_revenue)}
          width={barWidth}
          height={getBarHeight(data.forecast.total_revenue)}
          fill="url(#forecast-pattern)"
          opacity={0.2}
        />
      );
    }

    return bars;
  };

  return (
    <div className="bg-white rounded-lg border border-gray-200 p-6">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-lg font-semibold text-gray-900">Revenue</h3>
        {hasSegments && (
          <div className="flex flex-wrap items-center gap-3 text-xs">
            {data.segments.map((segment, i) => (
              <div key={segment} className="flex items-center gap-1.5">
                <div
                  className="w-3 h-3 rounded-sm"
                  style={{ backgroundColor: SEGMENT_COLORS[i % SEGMENT_COLORS.length] }}
                />
                <span className="text-gray-600">{segment}</span>
              </div>
            ))}
            {hasForecast && (
              <div className="flex items-center gap-1.5 ml-2 pl-2 border-l border-gray-300">
                <div className="w-3 h-3 rounded-sm bg-gray-400" />
                <span className="text-gray-500 italic">Forecast</span>
              </div>
            )}
          </div>
        )}
      </div>

      <svg
        ref={svgRef}
        width={width}
        height={height}
        className="overflow-visible"
        onMouseMove={handleMouseMove}
        onMouseLeave={handleMouseLeave}
      >
        {/* Defs for patterns */}
        <defs>
          <pattern id="forecast-pattern" width="8" height="8" patternUnits="userSpaceOnUse">
            <path d="M-1,1 l2,-2 M0,8 l8,-8 M7,9 l2,-2" stroke="#fff" strokeWidth="1.5" />
          </pattern>
        </defs>

        {/* Grid lines */}
        <g className="text-gray-200">
          {[0.25, 0.5, 0.75, 1].map((ratio) => (
            <line
              key={ratio}
              x1={padding.left}
              y1={padding.top + chartHeight * (1 - ratio)}
              x2={padding.left + chartWidth}
              y2={padding.top + chartHeight * (1 - ratio)}
              stroke="currentColor"
              strokeDasharray="4"
            />
          ))}
        </g>

        {/* Y-axis labels */}
        <g className="text-xs fill-gray-500">
          {yLabels.map((value, i) => (
            <text
              key={i}
              x={padding.left - 8}
              y={padding.top + chartHeight - (i * chartHeight / 4) + 4}
              textAnchor="end"
            >
              {formatShortBillions(value)}
            </text>
          ))}
        </g>

        {/* Bars */}
        {renderBars()}

        {/* X-axis labels */}
        <g className="text-xs fill-gray-500">
          {data.quarters.map((quarter, idx) => (
            <text
              key={quarter}
              x={getBarX(idx) + barWidth / 2}
              y={height - padding.bottom + 20}
              textAnchor="middle"
              transform={`rotate(-45, ${getBarX(idx) + barWidth / 2}, ${height - padding.bottom + 20})`}
            >
              {quarter}
            </text>
          ))}
          {hasForecast && data.forecast && (
            <text
              x={getBarX(data.quarters.length) + barWidth / 2}
              y={height - padding.bottom + 20}
              textAnchor="middle"
              transform={`rotate(-45, ${getBarX(data.quarters.length) + barWidth / 2}, ${height - padding.bottom + 20})`}
              className="fill-gray-400 italic"
            >
              {data.forecast.quarter} (F)
            </text>
          )}
        </g>

        {/* Hover highlight line */}
        {hoverInfo && (
          <line
            x1={getBarX(hoverInfo.index) + barWidth / 2}
            y1={padding.top}
            x2={getBarX(hoverInfo.index) + barWidth / 2}
            y2={padding.top + chartHeight}
            stroke="#6B7280"
            strokeWidth={1}
            strokeDasharray="4 2"
            opacity={0.5}
          />
        )}
      </svg>

      {/* Tooltip */}
      {hoverInfo && (
        <div
          className="absolute bg-white border border-gray-200 rounded-lg shadow-lg p-3 pointer-events-none z-10"
          style={{
            left: Math.min(hoverInfo.x + 10, width - 180),
            top: hoverInfo.y - 10,
            transform: 'translateY(-100%)',
          }}
        >
          <div className="text-sm font-semibold text-gray-900 mb-1">
            {hoverInfo.quarter}
            {hoverInfo.isForecast && (
              <span className="ml-1 text-xs font-normal text-gray-500">(Forecast)</span>
            )}
          </div>
          <div className="text-sm text-gray-700 font-medium">
            Total: {formatBillions(hoverInfo.totalRevenue)}
          </div>
          {hoverInfo.segments.length > 0 && (
            <div className="mt-2 space-y-1">
              {hoverInfo.segments.map((seg) => (
                <div key={seg.name} className="flex items-center justify-between gap-4 text-xs">
                  <div className="flex items-center gap-1.5">
                    <div
                      className="w-2 h-2 rounded-sm"
                      style={{ backgroundColor: seg.color }}
                    />
                    <span className="text-gray-600">{seg.name}</span>
                  </div>
                  <span className="text-gray-800 font-medium">
                    {formatBillions(seg.value)}
                  </span>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default RevenueChart;
